import { FunctionComponent } from "react";
import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";

import { CockpitCard } from "../design";

type Props = {
  totalCount: number;
  summary?: FlightSearchSummary | null;
  exportUrl: string;
};

export const FlightSearchSummary: FunctionComponent<Props> = ({ totalCount, summary, exportUrl }) => {
  const maxCount = Math.max(1, ...(summary?.hourlyBuckets || []).map((bucket) => bucket.count));
  return (
    <CockpitCard component="section" aria-labelledby="flight-results-summary" sx={{ p: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }}>
        <Box sx={{ minWidth: 175 }}>
          <Typography id="flight-results-summary" variant="h6">{totalCount} matching flights</Typography>
          <Typography variant="body2" sx={{ color: "var(--muted)" }}>
            {summary ? `${summary.selectedDayCount} on the selected day` : "Select an exact day for the Zulu histogram"}
          </Typography>
        </Box>
        {summary ? (
          <Box aria-label="Flights by Zulu departure hour" sx={{ flex: 1, minWidth: 0, overflowX: "auto" }}>
            <Stack direction="row" spacing={0.35} alignItems="flex-end" sx={{ minWidth: 480, height: 64 }}>
              {summary.hourlyBuckets.map((bucket) => (
                <Tooltip key={bucket.hour} title={`${String(bucket.hour).padStart(2, "0")}Z · ${bucket.count} flights${bucket.delayedCount ? ` · ${bucket.delayedCount} delayed` : ""}`}>
                  <Box sx={{ flex: 1, height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center" }}>
                    <Box sx={{ width: "100%", minHeight: bucket.count ? 3 : 1, height: `${Math.max(2, (bucket.count / maxCount) * 46)}px`, bgcolor: bucket.delayedCount ? "var(--amber)" : bucket.count ? "var(--blue)" : "var(--line)", borderRadius: "2px 2px 0 0" }} />
                    {bucket.hour % 3 === 0 ? <Typography sx={{ mt: 0.25, color: "var(--faint)", font: "500 9px var(--font-mono)" }}>{String(bucket.hour).padStart(2, "0")}</Typography> : <Box sx={{ height: 13 }} />}
                  </Box>
                </Tooltip>
              ))}
            </Stack>
          </Box>
        ) : <Box sx={{ flex: 1 }} />}
        <Button component="a" href={exportUrl} variant="outlined" startIcon={<DownloadOutlinedIcon />} sx={{ flexShrink: 0 }}>
          Export CSV
        </Button>
      </Stack>
    </CockpitCard>
  );
};
